import { ApiError, NetworkError } from '@/api/ApiError'
import { V3ApiContext } from './v3ApiContext'

const baseUrl = import.meta.env.VITE_API_BASE_URL.replace(/^(.+)\/api$/gm, '$1')

export type ErrorWrapper<TError> =
  | ApiError
  | NetworkError
  | TError
  | { status: 'unknown'; payload: string }

export type V3ApiFetcherOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string
  method: string
  body?: TBody
  headers?: THeaders
  queryParams?: TQueryParams
  pathParams?: TPathParams
  signal?: AbortSignal
} & V3ApiContext['fetcherOptions']

export async function v3ApiFetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
}: V3ApiFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  try {
    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
    }

    /**
     * As the fetch API is being used, when multipart/form-data is specified
     * the Content-Type header must be deleted so that the browser can set
     * the correct boundary.
     * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
     */
    if (
      requestHeaders['Content-Type']
        .toLowerCase()
        .includes('multipart/form-data')
    ) {
      delete requestHeaders['Content-Type']
    }

    const response = await window.fetch(
      `${baseUrl}${resolveUrl(url, queryParams, pathParams)}`,
      {
        signal,
        method: method.toUpperCase(),
        body: body
          ? body instanceof FormData
            ? body
            : JSON.stringify(body)
          : undefined,
        headers: requestHeaders,
      },
    )
    if (!response.ok) {
      // Replaced by postProcessCustomFetchers: preserve structured HTTP error info
      type _PreservedTError = ErrorWrapper<TError>
      throw await ApiError.fromResponse(response)
    }

    if (response.headers.get('content-type')?.includes('json')) {
      return await response.json()
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      return (await response.blob()) as unknown as TData
    }
  } catch (e) {
    // Replaced by postProcessCustomFetchers: don't double-wrap ApiError;
    // wrap genuine network failures (CORS, DNS, offline) in NetworkError.
    if (e instanceof ApiError) {
      throw e
    }
    throw new NetworkError(
      e instanceof Error ? e.message : 'Unknown network error',
      e instanceof Error ? e : undefined,
    )
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string> = {},
  pathParams: Record<string, string> = {},
) => {
  let query = new URLSearchParams(queryParams).toString()
  if (query) query = `?${query}`
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query
}
